import { Injectable } from '@angular/core';

// Formato que exige whisper.cpp (servicio Whisper, WHISPER.md §5.2): PCM 16 bits, mono, 16 kHz.
const TARGET_SAMPLE_RATE = 16000;

// Detección de "no hay voz" en el navegador, sin llamar al backend (WHISPER.md §5.2): el audio se
// trocea en ventanas de 100 ms y se mide su volumen (RMS, escala 0-1). Hay voz si al menos
// MIN_VOICE_WINDOWS ventanas superan el umbral — así un golpe o un clic suelto no cuentan como voz.
// Medido con el micrófono real en el navegador (2026-09-18): voz con mediana ~0,05 y picos ~0,2;
// silencio con máximo ~0,0006. Un ruido fuerte (golpe, puerta) sí supera el umbral: ningún umbral de
// volumen lo distingue de la voz — de eso se encarga el backend (filtro de "[Música]" y similares).
const VOICE_WINDOW_SECONDS = 0.1;
const VOICE_RMS_THRESHOLD = 0.01;
const MIN_VOICE_WINDOWS = 3;

// El micrófono tarda 1-2 s en dar audio real tras abrirlo: mientras tanto entrega ceros exactos y lo
// que se diga se pierde. Se espera a la primera muestra distinta de cero antes de empezar a grabar,
// con un máximo para no quedarse nunca atascado. Confirmado específico de Firefox (2026-09-21,
// comparando con un proyecto de referencia que graba al instante con el mismo dispositivo en Chrome,
// y con la misma app y el mismo Bluetooth funcionando sin espera al cambiar solo a Chrome): no se ha
// tocado la lógica porque, aunque en Chrome no haría falta, no molesta ahí (arranca casi al instante,
// el bucle de espera prácticamente no llega a iterar) y sigue haciendo falta en Firefox.
const MIC_READY_POLL_MS = 50;
const MIC_READY_TIMEOUT_MS = 3000;

export interface VoiceRecording {
  /** WAV PCM 16 bits mono 16 kHz, listo para mandar al backend. */
  wav: Blob;
  hasVoice: boolean;
}

/**
 * Grabación de mensajes de voz del chat: micrófono (MediaRecorder) y conversión del resultado a WAV
 * mono 16 kHz. Una sola grabación a la vez. El audio nunca se guarda: solo vive en memoria hasta
 * mandarlo a transcribir.
 *
 * Tres pasos, para poder mostrar "Preparando micrófono..." mientras el micrófono arranca:
 * open() → waitUntilReady() → startRecording(). cancel() vale en cualquier momento.
 */
@Injectable({ providedIn: 'root' })
export class VoiceRecorderService {
  private stream?: MediaStream;
  private recorder?: MediaRecorder;
  private chunks: Blob[] = [];
  // Se incrementa en cada open() y cancel(): un paso asíncrono que termina después de un cancel()
  // (p. ej. el permiso del micrófono concedido tras cancelar) sabe así que ya no debe hacer nada.
  private session = 0;

  isSupported(): boolean {
    return !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
  }

  /**
   * Pide el micrófono (sin grabar todavía). false si se canceló mientras tanto. Lanza error si el
   * usuario deniega el permiso o no hay micrófono.
   */
  async open(): Promise<boolean> {
    this.release();
    const session = ++this.session;

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, sampleRate: TARGET_SAMPLE_RATE }
    });

    if (session !== this.session) {
      stream.getTracks().forEach(track => track.stop());
      return false;
    }

    this.stream = stream;
    return true;
  }

  /**
   * Espera a que el micrófono abierto entregue audio real (primera muestra distinta de cero), como
   * mucho MIC_READY_TIMEOUT_MS. false si se canceló mientras tanto.
   */
  async waitUntilReady(): Promise<boolean> {
    const stream = this.stream;
    if (!stream) return false;
    const session = this.session;

    const context = new AudioContext();
    try {
      await context.resume();
      const analyser = context.createAnalyser();
      // Conectado a la salida a través de una ganancia 0 (no suena nada): así el navegador procesa
      // el grafo de audio y el analizador recibe muestras.
      const mute = context.createGain();
      mute.gain.value = 0;
      context.createMediaStreamSource(stream).connect(analyser);
      analyser.connect(mute);
      mute.connect(context.destination);

      const buffer = new Float32Array(analyser.fftSize);
      const startedAt = performance.now();

      while (performance.now() - startedAt < MIC_READY_TIMEOUT_MS) {
        if (session !== this.session) return false;

        analyser.getFloatTimeDomainData(buffer);
        if (buffer.some(sample => sample !== 0)) return true;
        await new Promise(resolve => setTimeout(resolve, MIC_READY_POLL_MS));
      }

      return session === this.session;
    } finally {
      void context.close();
    }
  }

  /** Empieza a grabar sobre el micrófono ya abierto. */
  startRecording(): void {
    if (!this.stream) throw new Error('El micrófono no está abierto');

    this.chunks = [];
    this.recorder = new MediaRecorder(this.stream);
    this.recorder.ondataavailable = event => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
    this.recorder.start();
  }

  /** Para la grabación, libera el micrófono y devuelve el audio convertido a WAV 16 kHz mono. */
  async stop(): Promise<VoiceRecording> {
    const recorder = this.recorder;
    if (!recorder || recorder.state === 'inactive') {
      this.release();
      throw new Error('No hay ninguna grabación en curso');
    }

    const recorded = await new Promise<Blob>(resolve => {
      recorder.onstop = () => resolve(new Blob(this.chunks, { type: recorder.mimeType }));
      recorder.stop();
    });
    this.release();

    const samples = await decodeToMono16k(recorded);
    return { wav: encodeWav(samples), hasVoice: hasVoice(samples) };
  }

  /** Descarta lo que haya en curso (preparación o grabación) y libera el micrófono. */
  cancel(): void {
    this.session++;
    if (this.recorder && this.recorder.state !== 'inactive') {
      this.recorder.onstop = null;
      this.recorder.stop();
    }
    this.release();
  }

  // Parar las pistas es lo que apaga el indicador de micrófono del navegador.
  private release(): void {
    this.stream?.getTracks().forEach(track => track.stop());
    this.stream = undefined;
    this.recorder = undefined;
    this.chunks = [];
  }
}

/**
 * Decodifica el audio del navegador (webm/opus, mp4...) directamente a 16 kHz: decodeAudioData
 * remuestrea a la frecuencia del contexto. Si viene en estéreo, se mezcla a mono.
 */
async function decodeToMono16k(blob: Blob): Promise<Float32Array> {
  const context = new OfflineAudioContext(1, 1, TARGET_SAMPLE_RATE);
  const buffer = await context.decodeAudioData(await blob.arrayBuffer());

  if (buffer.numberOfChannels === 1) return buffer.getChannelData(0);

  const mono = new Float32Array(buffer.length);
  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const data = buffer.getChannelData(channel);
    for (let i = 0; i < data.length; i++) mono[i] += data[i] / buffer.numberOfChannels;
  }
  return mono;
}

function hasVoice(samples: Float32Array): boolean {
  const windowSize = Math.round(TARGET_SAMPLE_RATE * VOICE_WINDOW_SECONDS);
  let voiceWindows = 0;

  for (let start = 0; start < samples.length; start += windowSize) {
    const end = Math.min(start + windowSize, samples.length);
    let sumSquares = 0;
    for (let i = start; i < end; i++) sumSquares += samples[i] * samples[i];

    if (Math.sqrt(sumSquares / (end - start)) > VOICE_RMS_THRESHOLD && ++voiceWindows >= MIN_VOICE_WINDOWS) return true;
  }
  return false;
}

/** WAV PCM 16 bits mono: cabecera RIFF de 44 bytes + muestras. */
function encodeWav(samples: Float32Array): Blob {
  const bytesPerSample = 2;
  const dataBytes = samples.length * bytesPerSample;
  const view = new DataView(new ArrayBuffer(44 + dataBytes));

  const writeString = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // tamaño del chunk fmt
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, TARGET_SAMPLE_RATE, true);
  view.setUint32(28, TARGET_SAMPLE_RATE * bytesPerSample, true); // byte rate
  view.setUint16(32, bytesPerSample, true); // block align
  view.setUint16(34, 16, true); // bits por muestra
  writeString(36, 'data');
  view.setUint32(40, dataBytes, true);

  for (let i = 0; i < samples.length; i++) {
    const sample = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * bytesPerSample, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }

  return new Blob([view], { type: 'audio/wav' });
}
